import React, { useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logo from '../../../assets/logos/logo.png';
import './InvoiceTemplate.css';

const convertToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const thousands = ['', 'Thousand', 'Million', 'Billion'];

  if (num === 0) return 'Zero';

  const convertHundreds = (n) => {
    let result = '';
    if (n > 99) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n > 19) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n > 9) {
      result += teens[n - 10] + ' ';
      return result;
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result;
  };

  let result = '';
  let thousandIndex = 0;

  while (num > 0) {
    if (num % 1000 !== 0) {
      result = convertHundreds(num % 1000) + thousands[thousandIndex] + ' ' + result;
    }
    num = Math.floor(num / 1000);
    thousandIndex++;
  }

  return result.trim();
};

const InvoiceTemplate = ({ invoice, onClose }) => {
  const invoiceRef = useRef(null);
  
  const generatePDF = async () => {
    if (!invoiceRef.current) return;

    try {
      // Try backend API first when available
      try {
        const response = await fetch('/api/invoices/generate-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoice }),
        });

        const contentType = response.headers.get('content-type') || '';
        if (response.ok && contentType.toLowerCase().includes('pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Skript_Invoice_${invoice.invoiceNumber}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          return;
        }
      } catch (apiError) {
        console.warn('Backend PDF generation failed, falling back to client-side:', apiError);
      }

      // Client-side: html2canvas + jsPDF (JPEG keeps file size down and avoids corrupt PDFs)
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // JPEG is smaller and more reliable in jsPDF than PNG for large content
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF('p', 'mm', 'a4');

      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      let heightLeft = imgHeight - pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Skript_Invoice_${invoice.invoiceNumber}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const calculateSubtotal = () => {
    if (!invoice.items || !Array.isArray(invoice.items) || invoice.items.length === 0) {
      return 0;
    }
    return invoice.items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      return sum + price;
    }, 0);
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    return invoice.useTax !== false ? subtotal * 0.11 : 0; // Use tax unless explicitly disabled
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    const discountRate = parseFloat(invoice.discount) || 0;
    return subtotal * (discountRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() - calculateDiscount();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="invoice-template-overlay">
      <div className="invoice-template-container">
        <div className="invoice-template-header">
          <h2>Invoice Preview</h2>
          <div className="invoice-template-actions">
            <button onClick={generatePDF} className="generate-pdf-btn">
              📄 Generate PDF
            </button>
            <button onClick={onClose} className="close-invoice-btn">
              ✕ Close
            </button>
          </div>
        </div>
        
        <div className="invoice-preview-container">
          <div ref={invoiceRef} className="invoice-template">
            {/* Header */}
            <div className="invoice-header">
              <div className="company-info">
                <img src={logo} alt="Skript Tech Logo" className="company-logo" />
                <div className="company-details">
                  <h1 className="company-name">Skript Tech</h1>
                  <p className="company-tagline">Empowering Digital Solutions</p>
                  <div className="company-contact">
                    <p>Beirut Malaab, Lebanon</p>
                    <p>support@skriptdev.com</p>
                    <p>www.skriptdev.com</p>
                    <p>Registration: 4039386</p>
                  </div>
                </div>
              </div>
              
              <div className="invoice-meta">
                <div className="invoice-number">
                  <p className="invoice-id">Inv. Id: {invoice.invoiceNumber || 'N/A'}</p>
                </div>
                <div className="invoice-date">
                  <p><strong>Date:</strong> {formatDate(invoice.date)}</p>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="client-section">
              <h3>Bill To:</h3>
              <div className="client-info">
                <p className="client-name">{invoice.clientName || 'N/A'}</p>
                <p className="client-email">{invoice.clientEmail || 'N/A'}</p>
                {invoice.clientPhone && <p className="client-phone">{invoice.clientPhone}</p>}
              </div>
            </div>

            {/* Items Table */}
            <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc', fontSize: '12px'}}>
              <thead>
                <tr>
                  <th style={{border: '1px solid #ccc', padding: '8px', textAlign: 'left', fontWeight: 'normal', fontSize: '12px'}}>Service</th>
                  <th style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontWeight: 'normal', fontSize: '12px'}}>
                    {invoice.useTax !== false ? 'Total (excl. VAT)' : 'Amount'}
                  </th>
                  <th style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontWeight: 'normal', fontSize: '12px'}}>Tax</th>
                  <th style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontWeight: 'normal', fontSize: '12px'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items && invoice.items.length > 0 ? (
                  invoice.items.map((item, index) => {
                    const itemTotal = parseFloat(item.price) || 0;
                    const taxAmount = invoice.useTax !== false ? itemTotal * 0.11 : 0; // Use tax unless explicitly disabled
                    const totalWithTax = itemTotal + taxAmount;
                    return (
                      <tr key={index}>
                        <td style={{border: '1px solid #ccc', padding: '8px', fontSize: '12px'}}>
                          {item.description || 'Professional Service'}
                        </td>
                        <td style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontSize: '12px'}}>
                          ${itemTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontSize: '12px'}}>
                          ${taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontSize: '12px'}}>
                          ${totalWithTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" style={{border: '1px solid #ccc', padding: '8px', textAlign: 'center', fontSize: '12px'}}>
                      No services have been added to this invoice
                    </td>
                  </tr>
                )}
                {invoice.items && invoice.items.length > 0 && (
                  <tr>
                    <td style={{border: '1px solid #ccc', padding: '8px', fontWeight: 'bold', fontSize: '12px'}} colSpan="3">Total</td>
                    <td style={{border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: '12px'}}>
                      ${invoice.items.reduce((sum, item) => {
                        const itemTotal = parseFloat(item.price) || 0;
                        const taxAmount = invoice.useTax !== false ? itemTotal * 0.11 : 0;
                        return sum + itemTotal + taxAmount;
                      }, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* VAT Section - Only show when tax is enabled */}
            {invoice.useTax !== false && (
              <div className="vat-section">
                <div className="vat-amount">
                  <p><strong>VAT (in LBP):</strong> {(invoice.items.reduce((sum, item) => {
                    const itemTotal = parseFloat(item.price) || 0;
                    const taxAmount = itemTotal * 0.11;
                    return sum + taxAmount;
                  }, 0) * 89500).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} LBP</p>
                </div>
                <div className="vat-words">
                  <p>{convertToWords(invoice.items.reduce((sum, item) => {
                    const itemTotal = parseFloat(item.price) || 0;
                    const taxAmount = itemTotal * 0.11;
                    return sum + taxAmount;
                  }, 0) * 89500)} Lebanese Pounds Only</p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="invoice-footer">
              <div className="footer-content">
                <p className="thank-you">Thank you for choosing Skript!</p>
                <p className="footer-note">We appreciate your business and look forward to serving you again.</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTemplate;

